import { LocalTime } from '@js-joda/core';

import { AvailabilityTypeSimple } from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';

import { commonTranslationKeys } from '@dreryk/react-common';
import {
   DatePicker,
   dayValidator,
   GridLayout,
   LargeAreaTemplate,
   RadioGroup,
   StyledFlex,
   TextView,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { personnelTranslationKeys } from '../../translations';
import { useDisabledDaySelector } from '../common/common.hooks';
import { FormType, RecurrencePeriod, RecurrencePeriodUnion, TimePickerRangeType } from '../common/common.types';
import { recurrenceTypes } from '../common/common.utils';
import { DocplannerServicesInformationNotification } from '../common/components/DocplannerServicesInformationNotification';
import { AvailabilityHealthcareProviderLocationSelect } from '../common/fields/AvailabilityHealthcareProviderLocation/AvailabilityHealthcareProviderLocationSelect';
import { AvailabilityTimeRange } from '../common/fields/AvailabilityTimeRange';
import { AvailabilityTimeSlotSection } from '../common/fields/AvailabilityTimeSlot';
import { SendToDocplanner } from '../common/fields/SendToDocplanner';
import { SendToPatientPortal } from '../common/fields/SendToPatientPortal';
import { EncounterModeCheckboxes } from '../common/fields/EncounterMode/EncounterModeCheckboxes';

import { AvailabilityTypeSelect } from './AvailabilityTypeSelect';
import { AvailabilityCreationFormController } from './availabilityCreation.types';

type AvailabilityCreationPanelProps = {
   readonly availabilityTypes: AvailabilityTypeSimple[];
   readonly register: AvailabilityCreationFormController['register'];
   readonly watch: AvailabilityCreationFormController['watch'];
   readonly formState: AvailabilityCreationFormController['formState'];
   readonly trigger: AvailabilityCreationFormController['trigger'];
   readonly recurrencePeriodSupplier: () => RecurrencePeriod;
   readonly adjustStartTimeOrEndTime: (rangeType: TimePickerRangeType, value: LocalTime, stepInMinutes: number) => void;
   readonly doctor: DoctorSimple | null;
   readonly healthcareLocations: HealthcareProviderLocation[];
};

const AVAILABILITY_CREATION_PANEL_GRID_AREA_WITH_SPECIALIST: LargeAreaTemplate = [
   'e1 e1 e1 . . . . . . . . . .',
   'e2 e2 e2 . . . . . . . . . .',
   'e3 e3 e3 e3 . . . . . . . . .',
   'e4 e4 e4 . . . . . . . . . .',
   'e5 e5 e5 . . . . . . . . . .',
   'e6 e6 e6 e6 e6 . . . . . . . .',
   'e7 e7 e7 e7 . . . . . . . . .',
   'e8 e8 e8 e8 . . . . . . . . .',
];
const AVAILABILITY_CREATION_PANEL_GRID_AREA: LargeAreaTemplate = [
   'e1 e1 e1 . . . . . . . . . .',
   'e2 e2 e2 e2 . . . . . . . . .',
   'e3 e3 e3  . . . . . . . . . .',
   'e4 e4 e4 . . . . . . . . . .',
   'e5 e5 e5 e5 e5 . . . . . . . .',
   'e6 e6 e6 e6 . . . . . . . . .',
   'e7 e7 e7 e7 . . . . . . . . .',
];

// eslint-disable-next-line max-lines-per-function
export const AvailabilityCreationPanel = (props: AvailabilityCreationPanelProps): JSX.Element => {
   const { availabilityTypes, register, watch, formState, trigger } = props;

   const { disabledDaySelector } = useDisabledDaySelector(props.recurrencePeriodSupplier, FormType.CREATION);

   const { sendToDocplanner, sendToPatientPortal } = props.watch(['sendToPatientPortal', 'sendToDocplanner']);

   return (
      <GridLayout
         area={
            props.doctor ? AVAILABILITY_CREATION_PANEL_GRID_AREA_WITH_SPECIALIST : AVAILABILITY_CREATION_PANEL_GRID_AREA
         }
         testId={'availabilityCreation'}
      >
         {!(props.doctor === null) && (
            <TextView label={personnelTranslationKeys.specialist} testId={'specialist'}>
               {props.doctor.firstName} {props.doctor.lastName}
            </TextView>
         )}
         <DatePicker
            testId={'availabilityDate'}
            label={commonTranslationKeys.date}
            controller={register('availabilityDate', {
               required: true,
            })}
            dayValidators={[dayValidator.custom(disabledDaySelector)]}
            withNavigationArrows
         />
         <AvailabilityTimeRange register={register} adjustStartTimeOrEndTime={props.adjustStartTimeOrEndTime} />
         <RadioGroup<RecurrencePeriodUnion>
            label={personnelTranslationKeys.recurrencePeriod}
            testId={'recurrencePeriod'}
            items={recurrenceTypes}
            controller={props.register('recurrencePeriod', {
               required: true,
            })}
         />
         <AvailabilityTypeSelect register={register} availabilityTypes={availabilityTypes} />
         <AvailabilityTimeSlotSection register={props.register} watch={watch} formState={formState} trigger={trigger} />
         <AvailabilityHealthcareProviderLocationSelect
            register={props.register}
            healthcareLocations={props.healthcareLocations}
         />
         <StyledFlex direction={'column'} gap={'12px'}>
            <SendToDocplanner register={props.register} trigger={trigger} />
            {protectFeature(
               'bookingVideoConsultations',
               sendToDocplanner ? (
                  <EncounterModeCheckboxes register={props.register} name={'docplannerEncounterMode'} watch={watch} />
               ) : null
            )}

            <SendToPatientPortal register={props.register} trigger={trigger} />
            {protectFeature(
               'bookingVideoConsultations',
               sendToPatientPortal ? (
                  <EncounterModeCheckboxes
                     register={props.register}
                     name={'patientPortalEncounterMode'}
                     watch={watch}
                  />
               ) : null
            )}
            <DocplannerServicesInformationNotification />
         </StyledFlex>
      </GridLayout>
   );
};
